// src/pages/EditCustomerProfile.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { uploadProfilePhoto } from '../services/firestoreService';
import ChangePassword from '../components/ChangePassword';
import ChangeEmail from '../components/ChangeEmail';
import DeleteAccountRequest from '../components/DeleteAccountRequest';
import BrandName from '../components/BrandName';
import './EditCustomerProfile.css';

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

const EditCustomerProfile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name:        user?.name        || '',
    phoneNumber: user?.phoneNumber || '',
    location:    user?.location    || '',
  });
  const [saving,         setSaving]         = useState(false);
  const [saved,          setSaved]          = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress,  setPhotoProgress]  = useState(0);
  const [photoSuccess,   setPhotoSuccess]   = useState(false);
  const [photoError,     setPhotoError]     = useState('');
  const [previewUrl,     setPreviewUrl]     = useState(user?.profilePhoto || null);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) { setPhotoError('Photo must be under 2 MB.'); return; }
    setPhotoError('');
    setPhotoSuccess(false);
    setPhotoUploading(true);
    setPhotoProgress(0);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const url = await uploadProfilePhoto(user.uid, file, setPhotoProgress);
      await updateUser({ profilePhoto: url });
      setPreviewUrl(url);
      setPhotoSuccess(true);
    } catch (err) {
      setPhotoError('Upload failed. Please try again.');
      console.error('[EditCustomerProfile] photo upload error:', err);
    }
    setPhotoUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await updateUser(form);
    setSaving(false);
    setSaved(true);
    setTimeout(() => navigate('/customer-dashboard'), 1200);
  };

  return (
    <div className="ecp-page">
      <div className="ecp-card">
        <div className="ecp-logo"><BrandName /></div>
        <h2>Edit Profile</h2>
        <p className="ecp-sub">Keep your details up to date</p>

        {/* PROFILE PHOTO */}
        <div className="ecp-section-label">Profile Photo</div>
        <div className="ecp-photo-section">
          <div className="ecp-avatar-wrap">
            {previewUrl
              ? <img src={previewUrl} alt="Profile" className="ecp-avatar-img" />
              : <div className="ecp-avatar-placeholder">{user?.name?.charAt(0) || '?'}</div>
            }
          </div>
          <div className="ecp-photo-controls">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={photoUploading}
            >
              {photoUploading ? `Uploading… ${photoProgress}%` : '📷 Change Photo'}
            </button>
            <p className="ecp-photo-hint">JPG or PNG, max 2 MB</p>
            {photoSuccess && <p className="ecp-photo-success">✓ Photo updated</p>}
            {photoError   && <p className="ecp-photo-error">{photoError}</p>}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
        </div>

        {/* CURRENT EMAIL — read-only display */}
        <div className="ecp-section-label" style={{ marginTop: '24px' }}>Account</div>
        <div className="ecp-locked-group">
          <div className="ecp-locked-field">
            <label className="ecp-label">Current Email</label>
            <div className="ecp-locked-value">{user?.email || '—'}</div>
          </div>
        </div>

        {/* EDITABLE FIELDS */}
        <div className="ecp-section-label" style={{ marginTop: '24px' }}>Your Details</div>
        <form onSubmit={handleSubmit}>
          <div className="ecp-form-group">
            <label className="ecp-label">Full Name</label>
            <input
              className="ecp-input"
              name="name"
              placeholder="Your name"
              value={form.name}
              onChange={handleChange}
            />
          </div>
          <div className="ecp-form-group">
            <label className="ecp-label">Phone Number</label>
            <input
              className="ecp-input"
              name="phoneNumber"
              type="tel"
              placeholder="e.g. 07700 900123"
              value={form.phoneNumber}
              onChange={handleChange}
            />
          </div>
          <div className="ecp-form-group">
            <label className="ecp-label">Location / Address</label>
            <input
              className="ecp-input"
              name="location"
              placeholder="e.g. Swansea, SA1"
              value={form.location}
              onChange={handleChange}
            />
          </div>

          <div className="ecp-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/customer-dashboard')}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || saved}
            >
              {saved ? '✓ SAVED' : saving ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
          </div>
        </form>

        <ChangePassword />
        <ChangeEmail />
        <DeleteAccountRequest />
      </div>
    </div>
  );
};

export default EditCustomerProfile;
